// Realtime notifications via WebSocket with auto-reconnect.
// Falls back gracefully if WS fails (consumers can still poll the REST endpoints).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function buildWsUrl() {
  try {
    const u = new URL(BACKEND_URL);
    const wsProto = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProto}//${u.host}/api/notifications/ws`;
  } catch {
    // Fallback: use current page origin
    const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProto}//${window.location.host}/api/notifications/ws`;
  }
}

export class NotificationsSocket {
  constructor({ token, onMessage, onOpen, onClose, onError }) {
    this.token = token;
    this.onMessage = onMessage || (() => {});
    this.onOpen = onOpen || (() => {});
    this.onClose = onClose || (() => {});
    this.onError = onError || (() => {});
    this.ws = null;
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.shouldRun = false;
    this.attempt = 0;
  }

  connect() {
    this.shouldRun = true;
    this._open();
  }

  _open() {
    if (!this.token) return;
    try {
      const url = `${buildWsUrl()}?token=${encodeURIComponent(this.token)}`;
      this.ws = new WebSocket(url);
    } catch (e) {
      this._scheduleReconnect();
      return;
    }
    this.ws.onopen = () => {
      this.attempt = 0;
      this.onOpen();
      // Heartbeat ping every 30 seconds so proxies don't drop the connection
      this.pingTimer = setInterval(() => {
        try { this.ws.send('ping'); } catch {}
      }, 30000);
    };
    this.ws.onmessage = (evt) => {
      if (evt.data === 'pong' || evt.data === 'ping') return;
      try {
        const data = JSON.parse(evt.data);
        this.onMessage(data);
      } catch {}
    };
    this.ws.onerror = (e) => { this.onError(e); };
    this.ws.onclose = () => {
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      this.onClose();
      if (this.shouldRun) this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    if (!this.shouldRun) return;
    this.attempt += 1;
    // Exponential backoff capped at 30s
    const delay = Math.min(30000, 1000 * 2 ** Math.min(this.attempt, 5));
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this._open(), delay);
  }

  close() {
    this.shouldRun = false;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    try { this.ws && this.ws.close(); } catch {}
    this.ws = null;
  }

  // Report genuine user activity so the server can distinguish an actively
  // working user from one who is merely connected but idle ("away").
  sendActivity() {
    try {
      if (this.ws && this.ws.readyState === 1) this.ws.send('activity');
    } catch { /* noop */ }
  }
}
